import { useGetCreditBalance } from '@/generated/hooks/mint/v1/credit_service';
import { getRoutePath } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { FEATURE_FLAGS } from '@/shared/types/features';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  cn,
  Flex,
  Icon,
} from '@kognitos/lattice';
import { useGateValue } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import {
  CREDIT_BALANCE_REFETCH_INTERVAL_MS,
  DISMISSED_ALERT_SESSION_KEY,
  LOW_CREDITS_THRESHOLD,
} from './constants';

type TCreditAlertVariant = 'low-balance' | 'out-of-credits';

interface ILowCreditsAlertProps {
  className?: string;
}

/**
 * Floating org-wide alert shown to every member once the organization's credit
 * balance drops below {@link LOW_CREDITS_THRESHOLD}, switching to the
 * destructive variant at zero. Dismissing either variant hides it for the
 * browser session; dismissing the low-balance warning does not hide a later
 * out-of-credits alert, and vice versa.
 */
export const LowCreditsAlert = ({ className }: ILowCreditsAlertProps) => {
  const { t } = useTranslation('organization');
  const navigate = useNavigate();
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);

  const [dismissedVariant, setDismissedVariant] =
    useSessionStorage<TCreditAlertVariant | null>(
      `${DISMISSED_ALERT_SESSION_KEY}:${orgId}`,
      null,
    );

  const isCreditsEnabled = useGateValue(
    FEATURE_FLAGS.ENABLE_CREDITS_CONSUMPTION_IN_UI,
  );

  const { data } = useGetCreditBalance(orgId, {
    query: {
      enabled: !!orgId && isCreditsEnabled,
      refetchInterval: CREDIT_BALANCE_REFETCH_INTERVAL_MS,
    },
  });

  if (!isCreditsEnabled || !orgId || !data) {
    return null;
  }

  const balance = data.data.balance ?? 0;
  if (balance >= LOW_CREDITS_THRESHOLD) {
    return null;
  }

  const isOutOfCredits = balance <= 0;
  const variant: TCreditAlertVariant = isOutOfCredits
    ? 'out-of-credits'
    : 'low-balance';
  if (dismissedVariant === variant) {
    return null;
  }

  const handleDismiss = () => setDismissedVariant(variant);

  const handleViewUsage = () => {
    navigate(getRoutePath('ORGANIZATION_CONSUMPTION', { orgId }));
  };

  return (
    <Alert
      variant={isOutOfCredits ? 'destructive' : 'default'}
      className={cn(
        'fixed top-4 right-4 z-40 flex w-96 max-w-[calc(100vw-2rem)] items-start gap-3 shadow-lg',
        className,
      )}
      data-testid="low-credits-alert"
    >
      <Icon
        type="AlertTriangle"
        className={cn('shrink-0', !isOutOfCredits && '!text-warning')}
      />
      <Flex direction="column" gap={1} className="flex-1 min-w-0 grow">
        <AlertTitle className="line-clamp-none">
          {isOutOfCredits
            ? t('organization.credits.outOfCredits.title')
            : t('organization.credits.lowBalance.title')}
        </AlertTitle>
        <AlertDescription>
          {isOutOfCredits
            ? t('organization.credits.outOfCredits.description')
            : t('organization.credits.lowBalance.description', { balance })}
        </AlertDescription>
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewUsage}
          data-testid="low-credits-alert-view-usage"
          className="mt-2 self-start"
        >
          {t('organization.credits.viewUsage')}
        </Button>
      </Flex>
      <Button
        variant="ghost"
        size="icon-xs"
        onClick={handleDismiss}
        aria-label={t('organization.credits.dismiss')}
        data-testid="low-credits-alert-dismiss"
        className="shrink-0"
      >
        <Icon type="X" />
      </Button>
    </Alert>
  );
};
